import { supabase } from '@/integrations/supabase/client';
import { Partner, ShowcaseProject } from '@/types/partner';
import { toast } from 'sonner';

export const fetchPartners = async (): Promise<Partner[]> => {
  const { data, error } = await supabase
    .from('partners')
    .select('id, name, logo, website, description, location, projects(*)')
    .order('name');
  
  if (error) {
    console.error('Error fetching partners:', error);
    throw error;
  }
  
  return data || [];
};

export const fetchPartnerById = async (id: string): Promise<Partner> => {
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // For mobile, try the same join query approach as desktop to bypass RLS
    const query = supabase
      .from('partners')
      .select(`
        *,
        projects(*)
      `)
      .eq('id', id)
      .single();
    
    const { data, error } = await query;
    
    if (error) {
      // Fallback to separate queries if join fails
      const { data: partnerData, error: partnerError } = await supabase
        .from('partners')
        .select('*')
        .eq('id', id)
        .single();
      
      if (partnerError) {
        throw partnerError;
      }

      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .eq('partner_id', id);
      
      if (projectsError) {
        console.error('Error fetching partner projects fallback:', projectsError);
        partnerData.projects = [];
      } else {
        partnerData.projects = projectsData || [];
      }

      return partnerData;
    }
    
    return data;
  } else {
    // For desktop, use the original join query
    const query = supabase
      .from('partners')
      .select(`
        *,
        projects(*)
      `)
      .eq('id', id)
      .single();
    
    const { data, error } = await query;
    
    if (error) {
      throw error;
    }
    
    return data;
  }
};

export const createPartner = async (partner: Omit<Partner, 'id' | 'created_at' | 'updated_at'>, logoFile?: File): Promise<Partner> => {
  try {
    let logoUrl = partner.logo;
    
    // If a file was provided, upload it to storage first
    if (logoFile) {
      console.log('Uploading logo file:', logoFile.name);
      
      // Generate a unique file name using timestamp and random string
      const timestamp = new Date().getTime();
      const randomString = Math.random().toString(36).substring(2, 10);
      const fileExtension = logoFile.name.split('.').pop();
      const fileName = `${timestamp}-${randomString}.${fileExtension}`;
      
      try {
        // Upload directly to the existing bucket without trying to create it
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('partners')
          .upload(`logos/${fileName}`, logoFile, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          throw uploadError;
        }
        
        // Get the public URL for the uploaded file
        const { data: urlData } = await supabase.storage
          .from('partners')
          .getPublicUrl(`logos/${fileName}`);
        
        logoUrl = urlData.publicUrl;
        console.log('Logo uploaded successfully:', logoUrl);
      } catch (storageError) {
        console.error('Error in logo upload:', storageError);
        toast.error('Could not upload logo. Using placeholder instead.');
        logoUrl = 'https://via.placeholder.com/300x150?text=Partner+Logo';
      }
    }
    
    // Remove projects from the partner data before inserting
    const { projects, ...partnerData } = partner;
    
    // Create the partner record with the logo URL
    const { data, error } = await supabase
      .from('partners')
      .insert([{
        ...partnerData,
        logo: logoUrl
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating partner:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Create partner error:', error);
    throw error;
  }
};

export const updatePartner = async (id: string, partner: Partial<Partner>, logoFile?: File): Promise<Partner> => {
  try {
    console.log('updatePartner called with:', { id, partner, logoFile });
    
    let logoUrl = partner.logo;
    
    // If a file was provided, upload it to storage first
    if (logoFile) {
      console.log('Uploading new logo file:', logoFile.name);
      
      // Generate a unique file name
      const timestamp = new Date().getTime();
      const randomString = Math.random().toString(36).substring(2, 10);
      const fileExtension = logoFile.name.split('.').pop();
      const fileName = `${timestamp}-${randomString}.${fileExtension}`;
      
      try {
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('partners')
          .upload(`logos/${fileName}`, logoFile, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }
        
        // Get the public URL for the uploaded file
        const { data: urlData } = await supabase.storage
          .from('partners')
          .getPublicUrl(`logos/${fileName}`);
        
        logoUrl = urlData.publicUrl;
        console.log('New logo uploaded successfully:', logoUrl);
      } catch (storageError) {
        console.error('Error uploading logo:', storageError);
        toast.error('Could not upload new logo. Keeping existing one.');
        // Don't throw error here, continue with existing logo
      }
    }
    
    // Prepare update data - ensure logo is always provided since it's NOT NULL in schema
    const updateData: any = {
      name: partner.name,
      website: partner.website || null,
      description: partner.description || null,
      location: partner.location || null,
      updated_at: new Date().toISOString(),
    };
    
    // Only update logo if we have a new URL
    if (logoUrl) {
      updateData.logo = logoUrl;
    }
    
    console.log('Updating partner with data:', updateData);
    
    // Update the partner record
    const { data, error } = await supabase
      .from('partners')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating partner:', error);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      console.error('Error message:', error.message);
      throw error;
    }
    
    console.log('Partner updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Update partner error:', error);
    throw error;
  }
};

export const deletePartner = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('partners')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting partner:', error);
    throw error;
  }
};

/** Homepage projects showcase — ordered by slot 1, 2, 3 */
export const fetchShowcaseProjects = async (): Promise<ShowcaseProject[]> => {
  const { data, error } = await supabase
    .from('projects')
    .select(
      'id, title, description, images, products, location, project_category, showcase, showcase_position, partner_id'
    )
    .eq('showcase', true)
    .not('showcase_position', 'is', null)
    .order('showcase_position', { ascending: true })
    .limit(3);

  if (error) {
    console.error('Error fetching showcase projects:', error);
    throw error;
  }

  return (data || []) as ShowcaseProject[];
};

/** Returns project occupying a homepage slot, if any */
export const fetchProjectByShowcasePosition = async (
  position: 1 | 2 | 3
): Promise<{ id: string; title: string } | null> => {
  const { data, error } = await supabase
    .from('projects')
    .select('id, title')
    .eq('showcase', true)
    .eq('showcase_position', position)
    .maybeSingle();

  if (error) {
    console.error('Error fetching showcase slot:', error);
    return null;
  }

  return data;
};

export const clearShowcasePosition = async (position: 1 | 2 | 3, exceptProjectId?: string) => {
  let query = supabase
    .from('projects')
    .update({ showcase: false, showcase_position: null })
    .eq('showcase_position', position);

  if (exceptProjectId) {
    query = query.neq('id', exceptProjectId);
  }

  const { error } = await query;
  if (error) {
    console.error('Error clearing showcase position:', error);
    // If column missing, surface a clear message
    if (String(error.message || '').includes('showcase_position')) {
      throw new Error(
        'Column showcase_position is missing. Run the SQL migration on Supabase.'
      );
    }
    throw error;
  }
};

/** Ensure only one projects-page hero exists */
export const clearProjectsHero = async (exceptProjectId?: string) => {
  let query = supabase
    .from('projects')
    .update({ is_projects_hero: false })
    .eq('is_projects_hero', true);

  if (exceptProjectId) {
    query = query.neq('id', exceptProjectId);
  }

  const { error } = await query;
  if (error) {
    console.error('Error clearing projects hero:', error);
    throw error;
  }
};
